import type { CSSProperties } from "react";
import { cn } from "@/lib/utils";
export type FaceMood =
  | "content"
  | "working"
  | "thinking"
  | "reading"
  | "browsing"
  | "creating"
  /** The ball's own (spec 2026-09-12 bash-holds-it §2): Bash asking Nami, the
   *  memory. Only the ball dresses it (bash-home.css, under .cl-orb); nothing
   *  outside the ball wears it. */
  | "recalling"
  | "waiting"
  | "happy"
  | "puzzled"
  | "listening"
  | "sleeping"
  | "waking"
  /** The ball's rest (spec 2026-09-12 §2): awake, calm, the eyes on the cursor. */
  | "present";

/**
 * The face itself: the three strokes, plus the shapes a mood shows. Lifted out
 * of BashFace so the kin (KinFace, spec 2026-09-06 §3.4) draws the same face
 * instead of a copy of it. The copper box and the brand's meaning stay with
 * BashFace; this is only the drawing.
 */
export function FacePaths({ delay = "-1.5s" }: { delay?: string }) {
  return (
    // The kin's stroke rides --kin-ink instead of the brand's literal copper, and
    // that is the PARENT's business: `.bh-face.bh-face--kin svg` (globals.css) is
    // (0,2,1) and outranks `.bh-face svg`'s copper. A marker class here would be
    // (0,1,0) and lose to that same rule, which is what left every kin face wearing
    // Bash's copper on staging — so the drawing carries no kin flag at all.
    <svg viewBox="15.5 7.5 33 24.5">
      <g
        className="bh-face__eyes transition-bh-face-move"
        style={{ "--fd": delay } as CSSProperties}
      >
        <g className="bh-face__eye transition-bh-face-blink">
          <path
            className="bh-face__up transition-bh-face-fade"
            d="M18 17 L24 10 L30 17"
          />
          <path
            className="bh-face__lid transition-bh-face-fade"
            d="M18.5 14.5 L29.5 14.5"
          />
          <circle
            className="bh-face__open transition-bh-face-fade"
            cx="24"
            cy="13.6"
            r="3.3"
          />
          <circle
            className="bh-face__pupil transition-bh-face-fade"
            cx="24"
            cy="13.6"
            r="1.4"
          />
        </g>
        <g className="bh-face__eye transition-bh-face-blink">
          <path
            className="bh-face__up transition-bh-face-fade"
            d="M34 17 L40 10 L46 17"
          />
          <path
            className="bh-face__lid transition-bh-face-fade"
            d="M34.5 14.5 L45.5 14.5"
          />
          <circle
            className="bh-face__open transition-bh-face-fade"
            cx="40"
            cy="13.6"
            r="3.3"
          />
          <circle
            className="bh-face__pupil transition-bh-face-fade"
            cx="40"
            cy="13.6"
            r="1.4"
          />
        </g>
      </g>
      <g className="bh-face__mouth transition-bh-face-move">
        <path
          className="bh-face__smile transition-bh-face-fade"
          d="M24 26 Q32 33 40 26"
        />
        <path
          className="bh-face__flat transition-bh-face-fade"
          d="M27.5 27.5 L36.5 27.5"
        />
        <path
          className="bh-face__o transition-bh-face-fade"
          d="M29.2 27.2 a2.8 2.8 0 1 0 5.6 0 a2.8 2.8 0 1 0 -5.6 0"
        />
        <path
          className="bh-face__wave transition-bh-face-fade"
          d="M26.5 27.5 q2.8 -2.6 5.5 0 t5.5 0"
        />
        <path
          className="bh-face__grin transition-bh-face-fade"
          d="M22.5 25 Q32 35.5 41.5 25"
        />
      </g>
    </svg>
  );
}

/** The box each size draws: 32/10, 48/14, 24/7, 30/9 (width+height / radius). */
const FACE_SIZE: Record<"sm" | "big" | "nav" | "desk", string> = {
  sm: "size-8 rounded-[10px]",
  big: "size-12 rounded-[14px]",
  nav: "size-6 rounded-[7px]",
  desk: "size-7.5 rounded-[9px]",
};

/**
 * The Bash brand mark — the copper face from the landing (public/landing-d6/
 * d7-reimagine.html .b-mark), lifted 1:1: copper squircle, two ^ eyes that
 * blink on the landing's 5.4s clock, one smile. Founder (08-26): "bash branding
 * is the copper avatar we built for landing, not the cursor". Sizes: sm 32
 * (pill line, mobile bar), big 48 (Activity card), nav 24, desk 30.
 * Moods (task screen, 08-29): the same three strokes plus one movement each;
 * the extra shapes below stay invisible until a mood shows them (bash-home.css).
 */
export function BashFace({
  size = "sm",
  mood = "content",
  delay = "-1.5s",
  className,
}: {
  size?: "sm" | "big" | "nav" | "desk";
  mood?: FaceMood;
  delay?: string;
  className?: string;
}) {
  return (
    <span
      className={cn("bh-face", `bh-face--${size}`, FACE_SIZE[size], className)}
      data-mood={mood}
      aria-hidden="true"
      data-testid="bh-face"
    >
      <FacePaths delay={delay} />
    </span>
  );
}
